import { Label } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Merge } from 'lucide-react'
import type { SkillInput, SavedSkill } from '@creanizer/types'

interface SkillFormCombinationProps {
  input: SkillInput
  onChange: (input: SkillInput) => void
  savedSkills: SavedSkill[]
  selectedCombinationSkills: string[]
  setSelectedCombinationSkills: (ids: string[]) => void
}

export function SkillFormCombination({
  input,
  onChange,
  savedSkills,
  selectedCombinationSkills,
  setSelectedCombinationSkills
}: SkillFormCombinationProps) {
  const generateSkillContext = (skill: SavedSkill) => {
    return JSON.stringify({
      name: skill.result.basicInfo.name,
      type: skill.result.basicInfo.type,
      category: skill.result.basicInfo.category,
      description: skill.result.basicInfo.description,
      effects: skill.result.effects.primaryEffect,
      mechanics: {
        activationType: skill.result.mechanics.activationType,
        targetType: skill.result.mechanics.targetType,
        cost: skill.result.mechanics.cost
      }
    })
  }
  
  return (
    <div className="space-y-6">
      <Alert>
        <Merge className="h-4 w-4" />
        <AlertDescription>
          複数のスキルを組み合わせて新しい融合スキルを生成します。
          各スキルの特徴を活かした相乗効果のあるスキルが作成されます。
        </AlertDescription>
      </Alert>
      
      {savedSkills.length < 2 ? (
        <Alert variant="destructive">
          <AlertDescription>
            組み合わせるスキルが不足しています。先に標準モードで2つ以上のスキルを生成してください。
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="space-y-2">
            <Label>組み合わせるスキル（2つ以上選択）</Label>
            <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4">
              {savedSkills.map((skill) => (
                <div key={skill.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={skill.id}
                    checked={selectedCombinationSkills.includes(skill.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        const newSkills = [...selectedCombinationSkills, skill.id]
                        setSelectedCombinationSkills(newSkills)
                        const contexts = newSkills.map(id => {
                          const s = savedSkills.find(sk => sk.id === id)
                          return s ? generateSkillContext(s) : ''
                        }).filter(Boolean)
                        onChange({ ...input, combinationSkills: contexts })
                      } else {
                        const newSkills = selectedCombinationSkills.filter(id => id !== skill.id)
                        setSelectedCombinationSkills(newSkills)
                        const contexts = newSkills.map(id => {
                          const s = savedSkills.find(sk => sk.id === id)
                          return s ? generateSkillContext(s) : ''
                        }).filter(Boolean)
                        onChange({ ...input, combinationSkills: contexts })
                      }
                    }}
                  />
                  <Label htmlFor={skill.id} className="flex-1 cursor-pointer">
                    <div>
                      <div className="font-semibold">{skill.result.basicInfo.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {skill.result.basicInfo.type} / {skill.result.basicInfo.category}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {skill.result.basicInfo.summary}
                      </div>
                    </div>
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          {selectedCombinationSkills.length > 0 && selectedCombinationSkills.length < 2 && (
            <Alert variant="destructive">
              <AlertDescription>
                最低2つのスキルを選択してください
              </AlertDescription>
            </Alert>
          )}
          
          {selectedCombinationSkills.length >= 2 && (
            <Alert>
              <AlertDescription>
                {selectedCombinationSkills.length}個のスキルを組み合わせて新しいスキルを生成します。
                選択したスキルの特性が融合された強力なスキルが作成されます。
              </AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  )
}